export interface CaseStudy {
  slug: string;
  name: string;
  tagline: { nl: string; en: string };
  heroImage: string;
  imagePosition?: string;
  vertical: string;
  services: string[];
  goals: { nl: string[]; en: string[] };
  approach: { nl: string[]; en: string[] };
  results: { label: { nl: string; en: string }; value: string }[];
  description: { nl: string; en: string };
  channels: string[];
  quote?: { text: { nl: string; en: string }; author: string; role: string; image?: string };
  website?: string;
  resultsTitle?: { nl: string; en: string };
  timeline?: { marker: string; title: { nl: string; en: string }; text: { nl: string; en: string } }[];
  story?: { nl: string[]; en: string[] };
  media?: { type: "video" | "image"; src: string }[];
}

export const verticals = {
  nl: ["Alle verticals", "Fashion & Sieraden", "Beauty & Verzorging", "Home & Living", "Mobiliteit", "Entertainment", "Educatie", "Publieke sector"],
  en: ["All verticals", "Fashion & Jewelry", "Beauty & Care", "Home & Living", "Mobility", "Entertainment", "Education", "Public sector"],
};

export const serviceFilters = {
  nl: ["Alle diensten", "Meta Advertising", "TikTok Advertising", "Google Ads", "Klaviyo", "Pinterest Ads", "LinkedIn Advertising"],
  en: ["All services", "Meta Advertising", "TikTok Advertising", "Google Ads", "Klaviyo", "Pinterest Ads", "LinkedIn Advertising"],
};

export const cases: CaseStudy[] = [
  {
    slug: "jutka-riska",
    name: "Jutka & Riska",
    tagline: {
      nl: "Iconisch winkelmerk bouwt aan een online groeimotor",
      en: "Iconic retail brand building an online growth engine",
    },
    heroImage: "/cases/jutka-riska.webp",
    vertical: "Fashion & Sieraden",
    services: ["Meta Advertising", "Klaviyo"],
    goals: {
      nl: [
        "Online omzet opbouwen naast de vier fysieke winkels",
        "Een volwaardige e-commerce funnel neerzetten: van advertising tot e-mail en conversie",
        "Campagnemomenten structureel benutten, van ELLE Shopping tot Black Friday",
      ],
      en: [
        "Build online revenue alongside the four physical stores",
        "Create a complete e-commerce funnel: from advertising to email and conversion",
        "Structurally leverage campaign moments, from ELLE Shopping to Black Friday",
      ],
    },
    approach: {
      nl: [
        "Meta-campagnes rond de kracht van het merk: vintage selectie en nieuwe collecties",
        "CRM-strategie en Klaviyo-implementatie: pop-up, flows en campagnes",
        "Conversie-optimalisatieplan voor de webshop",
        "Jaarplanning met campagnemomenten en inkoopadvies op basis van verkoopdata",
      ],
      en: [
        "Meta campaigns around the brand's strength: vintage selection and new collections",
        "CRM strategy and Klaviyo implementation: pop-up, flows and campaigns",
        "Conversion optimization plan for the webshop",
        "Annual planning with campaign moments and buying advice based on sales data",
      ],
    },
    results: [
      { label: { nl: "Online omzet (jan-jul, j-o-j)", en: "Online revenue (Jan-Jul, YoY)" }, value: "+217%" },
      { label: { nl: "ROAS", en: "ROAS" }, value: "7+" },
      { label: { nl: "Gemiddelde orderwaarde", en: "Average order value" }, value: "+42%" },
      { label: { nl: "Orders (j-o-j)", en: "Orders (YoY)" }, value: "+145%" },
    ],
    description: {
      nl: "Jutka & Riska is een iconisch Amsterdams fashionmerk met vier winkels in Amsterdam, Haarlem en Antwerpen. Het merk combineert nieuwe collecties met handgeselecteerde vintage.\n\nSinds september 2025 bouwen we samen aan de online groeimotor naast de winkels. Niet alleen advertising: we implementeerden Klaviyo, stelden een conversie-optimalisatieplan op en plannen het hele jaar rond campagnemomenten als ELLE Shopping, Black Friday en de feestdagen.\n\nDe samenwerking gaat verder dan marketing: op basis van verkoopdata adviseren we ook over voorraad en inkoop voor de webshop - van hero-producten tot maatvoering.\n\nHet resultaat na de eerste maanden van 2026: online omzet ruim verdrievoudigd ten opzichte van een jaar eerder, 145% meer orders, een 42% hogere orderwaarde en een ROAS boven de 7.",
      en: "Jutka & Riska is an iconic Amsterdam fashion brand with four stores in Amsterdam, Haarlem and Antwerp. The brand combines new collections with hand-picked vintage.\n\nSince September 2025 we have been building the online growth engine alongside the stores. Not just advertising: we implemented Klaviyo, created a conversion optimization plan and plan the entire year around campaign moments like ELLE Shopping, Black Friday and the holidays.\n\nThe partnership goes beyond marketing: based on sales data we also advise on inventory and buying for the webshop - from hero products to sizing.\n\nThe result after the first months of 2026: online revenue more than tripled year-on-year, 145% more orders, a 42% higher order value and a ROAS above 7.",
    },
    channels: ["Meta Advertising", "Klaviyo", "CRO", "Google Analytics", "Google Tag Manager"],
    website: "https://jutkaenriska.com/",
    resultsTitle: {
      nl: "Elk campagnemoment benut.",
      en: "Every campaign moment used.",
    },
    timeline: [
      { marker: "Sep 2025", title: { nl: "Start samenwerking", en: "Partnership start" }, text: { nl: "CRO-plan voor de webshop en de basis voor de CRM-strategie.", en: "CRO plan for the webshop and the foundation for the CRM strategy." } },
      { marker: "Okt 2025", title: { nl: "Funnel live", en: "Funnel live" }, text: { nl: "Meta-campagnes live, Klaviyo ge\u00efmplementeerd en de Q4-planning rond ELLE Shopping, Black Friday en de feestdagen.", en: "Meta campaigns live, Klaviyo implemented and Q4 planning around ELLE Shopping, Black Friday and the holidays." } },
      { marker: "Q4", title: { nl: "Campagneseizoen", en: "Campaign season" }, text: { nl: "Black Friday en feestdagencampagnes over de volle breedte: ads, e-mail en on-site.", en: "Black Friday and holiday campaigns across the board: ads, email and on-site." } },
      { marker: "2026", title: { nl: "Jaarplanning & inkoopadvies", en: "Annual planning & buying advice" }, text: { nl: "Volledige jaarplanning met campagnemomenten, plus voorraad- en inkoopadvies op basis van verkoopdata.", en: "Full annual planning with campaign moments, plus inventory and buying advice based on sales data." } },
      { marker: "H1 2026", title: { nl: "Omzet ruim 3x", en: "Revenue more than 3x" }, text: { nl: "Online omzet +217% ten opzichte van een jaar eerder, met 145% meer orders, 42% hogere orderwaarde en een ROAS boven de 7.", en: "Online revenue +217% year-on-year, with 145% more orders, 42% higher order value and a ROAS above 7." } },
    ],
  },
  {
    slug: "het-juridisch-loket",
    name: "Het Juridisch Loket",
    tagline: {
      nl: "Gratis rechtshulp vindbaar voor heel Nederland",
      en: "Free legal aid findable for all of the Netherlands",
    },
    heroImage: "/cases/het-juridisch-loket.webp",
    imagePosition: "center 65%",
    vertical: "Publieke sector",
    services: ["Google Ads", "Meta Advertising"],
    goals: {
      nl: [
        "Naamsbekendheid vergroten onder mensen met recht op gesubsidieerde rechtsbijstand (circa 36% van Nederland)",
        "Meer contactmomenten en bezoek aan de vestigingen realiseren",
        "Het merk beschermen in de zoekresultaten tegen commerci\u00eble aanbieders",
      ],
      en: [
        "Increase brand awareness among people entitled to subsidized legal aid (roughly 36% of the Netherlands)",
        "Drive more contact moments and visits to local branches",
        "Protect the brand in search results against commercial providers",
      ],
    },
    approach: {
      nl: [
        "Merkbescherming op 800.000+ branded zoekopdrachten per jaar",
        "Thematische zoekcampagnes rond juridische vragen: van huurtoeslag tot alimentatie",
        "Locatiecampagnes per vestiging en Performance Max op hulpthema's",
        "Awareness- en wervingscampagnes op Meta, getarget met lookalike audiences",
      ],
      en: [
        "Brand protection on 800,000+ branded searches per year",
        "Thematic search campaigns around legal questions: from housing benefit to alimony",
        "Location campaigns per branch and Performance Max on help topics",
        "Awareness and recruitment campaigns on Meta, targeted with lookalike audiences",
      ],
    },
    results: [
      { label: { nl: "Samenwerking", en: "Partnership" }, value: "4+ jaar" },
      { label: { nl: "Branded zoekopdrachten per jaar", en: "Branded searches per year" }, value: "800K+" },
      { label: { nl: "Doelgroep", en: "Audience" }, value: "36% van NL" },
    ],
    description: {
      nl: "Het Juridisch Loket is d\u00e9 landelijke organisatie voor gratis juridische hulp. Sinds 2022 verzorgen wij de volledige online advertising: van merkbescherming en thematische zoekcampagnes tot awareness- en wervingscampagnes op Meta.\n\nDe doelgroep is breed: iedereen met recht op gesubsidieerde rechtsbijstand, circa 36% van Nederland. Dat vraagt om een andere aanpak dan e-commerce: geen ROAS, maar maatschappelijke impact - meetbaar in bereik, contactmomenten en vestigingsbezoek.\n\nWat begon met een advertisingstrategie voor 2022 is uitgegroeid tot een samenwerking van ruim vier jaar, waarin we jaarlijks samen plannen, testen en optimaliseren.",
      en: "Het Juridisch Loket is the national organization for free legal aid in the Netherlands. Since 2022 we have managed all online advertising: from brand protection and thematic search campaigns to awareness and recruitment campaigns on Meta.\n\nThe audience is broad: everyone entitled to subsidized legal aid, roughly 36% of the Netherlands. That requires a different approach than e-commerce: not ROAS, but social impact - measured in reach, contact moments and branch visits.\n\nWhat started as an advertising strategy for 2022 has grown into a partnership of more than four years, planning, testing and optimizing together every year.",
    },
    channels: ["Google Ads", "Meta Advertising", "Performance Max", "Google Analytics"],
    website: "https://www.juridischloket.nl/",
    resultsTitle: {
      nl: "Zichtbaar op elk juridisch moment.",
      en: "Visible at every legal moment.",
    },
    timeline: [
      { marker: "2022", title: { nl: "Start samenwerking", en: "Partnership start" }, text: { nl: "Strategie voor online advertising: doelgroepanalyse, kanaalkeuze en doelstellingen voor naamsbekendheid, contact en merkbescherming.", en: "Online advertising strategy: audience analysis, channel choices and objectives for awareness, contact and brand protection." } },
      { marker: "Search", title: { nl: "Merkbescherming", en: "Brand protection" }, text: { nl: "Branded campagnes beschermen 800.000+ zoekopdrachten per jaar, zodat hulpzoekers niet bij commerci\u00eble aanbieders uitkomen.", en: "Branded campaigns protect 800,000+ searches per year, so people seeking help do not end up with commercial providers." } },
      { marker: "Thema's", title: { nl: "Thematische campagnes", en: "Thematic campaigns" }, text: { nl: "Campagnes rond juridische vragen en actualiteit: huurtoeslag, alimentatie, arbeidsrecht en meer dan 300 relevante zoektermen.", en: "Campaigns around legal questions and current events: housing benefit, alimony, employment law and 300+ relevant search terms." } },
      { marker: "2024", title: { nl: "Verbreding", en: "Broadening" }, text: { nl: "Performance Max, locatiecampagnes per vestiging en wervingscampagnes voor nieuwe medewerkers via Meta.", en: "Performance Max, location campaigns per branch and recruitment campaigns for new employees via Meta." } },
      { marker: "Nu", title: { nl: "Ruim 4 jaar partner", en: "4+ years as partner" }, text: { nl: "Jaarlijkse strategie, maandelijkse rapportages en doorlopende optimalisatie.", en: "Annual strategy, monthly reporting and continuous optimization." } },
    ],
    story: {
      nl: [
        "Het Juridisch Loket is een onafhankelijke stichting, gefinancierd door het Ministerie van Justitie en Veiligheid, die gratis juridisch advies geeft aan burgers met een laag inkomen en weinig vermogen. Jaarlijks helpt de organisatie honderdduizenden Nederlanders. De primaire doelgroep: iedereen met recht op gesubsidieerde rechtsbijstand - circa 36% van de Nederlandse bevolking.",
        "In 2022 ontwikkelden we de online advertisingstrategie. De uitdaging: platformen als Meta staan targeting op inkomen niet toe. We losten dat op met lookalike audiences van hulpzoekers.",
        "Op zoekgebied bleek de merknaam kwetsbaar: op de 800.000+ jaarlijkse zoekopdrachten naar Het Juridisch Loket adverteerden commerci\u00eble rechtshulpaanbieders. Branded campagnes beschermen die zoekvraag, zodat mensen die recht hebben op gratis hulp daar ook terechtkomen. Thematische campagnes vangen daarnaast de vraag op rond onderwerpen als huurtoeslag, alimentatie en arbeidsrecht.",
        "Inmiddels werken we ruim vier jaar samen. De aanpak is verbreed met Performance Max, locatiecampagnes per vestiging en wervingscampagnes op Meta. Elk jaar een strategie, elke maand een rapportage - en doorlopend bijsturen op wat werkt.",
      ],
      en: [
        "Het Juridisch Loket is an independent foundation, funded by the Dutch Ministry of Justice and Security, providing free legal advice to citizens with a low income and limited assets. Every year the organization helps hundreds of thousands of Dutch citizens. The primary audience: everyone entitled to subsidized legal aid - roughly 36% of the Dutch population.",
        "In 2022 we developed the online advertising strategy. The challenge: platforms like Meta do not allow targeting on income. We solved this with lookalike audiences of help seekers.",
        "In search, the brand name proved vulnerable: commercial legal aid providers were advertising on the 800,000+ annual searches for Het Juridisch Loket. Branded campaigns protect that search demand, so people entitled to free help actually get there. Thematic campaigns capture demand around topics like housing benefit, alimony and employment law.",
        "We have now been working together for more than four years. The approach has broadened with Performance Max, location campaigns per branch and recruitment campaigns on Meta. A strategy every year, a report every month - and continuous optimization.",
      ],
    },
  },
  {
    slug: "mimi-toi",
    name: "Mimi & Toi",
    tagline: {
      nl: "Trendy sieraden met seizoensgebonden piekverkoop",
      en: "Trendy jewelry with seasonal peak sales",
    },
    heroImage: "/cases/mimi-toi.jpeg",
    vertical: "Fashion & Sieraden",
    services: ["Meta Advertising", "Google Ads", "Klaviyo CRM", "CRO Advies"],
    goals: {
      nl: [
        "Online omzet verhogen via cross-channel advertising",
        "Seizoenscampagnes maximaal benutten",
        "Opschalen in meer dan 8 Europese landen",
      ],
      en: [
        "Increase online revenue through cross-channel advertising",
        "Maximize seasonal campaigns",
        "Scale across more than 8 European countries",
      ],
    },
    approach: {
      nl: [
        "Cross-channel strategie: META en Google Ads in 8+ Europese landen",
        "Seizoenscampagnes: Valentijnsdag, Black Friday, Kerst",
        "E-mailmarketing en flows via Klaviyo CRM",
        "CRO-advies en funnel optimalisatie van ad tot checkout",
      ],
      en: [
        "Cross-channel strategy: META and Google Ads across 8+ European countries",
        "Seasonal campaigns: Valentine's Day, Black Friday, Christmas",
        "Email marketing and flows via Klaviyo CRM",
        "CRO advice and funnel optimization from ad to checkout",
      ],
    },
    results: [
      { label: { nl: "Omzetgroei per jaar", en: "Revenue growth per year" }, value: "120%+" },
      { label: { nl: "Jaarlijks bereik", en: "Annual reach" }, value: "2M+" },
      { label: { nl: "Piekresultaten", en: "Peak results" }, value: "BF & Kerst" },
    ],
    description: {
      nl: "Mimi & Toi combineert trendy, betaalbare sieraden met een speelse designtaal. De collecties wisselen snel en spelen in op de laatste modetrends.\n\nSinds september 2024 zetten we een cross-channel strategie op met META en Google Ads, met campagnes in meer dan 8 Europese landen. Door per kanaal, land en seizoen te testen vonden we de optimale combinatie. Seizoenscampagnes rondom Valentijnsdag, Black Friday en Kerst leveren sterke piekresultaten. Daarnaast verzorgen we e-mailmarketing via Klaviyo en adviseren we over conversie-optimalisatie (CRO).\n\nDe online omzet groeit structureel met meer dan 120% per jaar en het bereik via social ads is uitgegroeid tot meer dan 2 miljoen per jaar.",
      en: "Mimi & Toi combines trendy, affordable jewelry with a playful design language. Collections change quickly, keeping up with the latest fashion trends.\n\nSince September 2024 we run a cross-channel strategy with META and Google Ads, with campaigns across more than 8 European countries. By testing per channel, country and season, we found the optimal combination. Seasonal campaigns around Valentine's Day, Black Friday, and Christmas deliver strong peak results. We also run email marketing via Klaviyo and advise on conversion rate optimization (CRO).\n\nOnline revenue grows structurally by more than 120% per year and reach through social ads has grown to over 2 million per year.",
    },
    channels: ["Meta Advertising", "Google Ads", "Klaviyo", "Databox", "Google Analytics", "Google Tag Manager"],
    website: "https://mimiettoi.com/",
  },
  {
    slug: "moddern",
    name: "Moddern",
    tagline: {
      nl: "Premium kamado-merk, full-funnel vanaf seizoen \u00e9\u00e9n",
      en: "Premium kamado brand, full-funnel from season one",
    },
    heroImage: "/cases/moddern.webp",
    vertical: "Home & Living",
    services: ["Meta Advertising", "Google Ads", "Klaviyo"],
    goals: {
      nl: [
        "Een nieuw premium merk vanaf de lancering op de kaart zetten",
        "Renderende funnel bouwen in het eerste seizoen",
        "Merkopbouw en verkoop laten samenwerken",
      ],
      en: [
        "Put a new premium brand on the map from launch",
        "Build a profitable funnel in the first season",
        "Make brand building and sales work together",
      ],
    },
    approach: {
      nl: [
        "Full-funnel setup vanaf dag \u00e9\u00e9n: Meta, Google Ads en Klaviyo",
        "Creative-strategie rond het product in gebruik: koken, buiten leven, seizoen",
        "CRO op de webshop en doorlopende A/B-tests",
        "Seizoensplanning afgestemd op het buitenkook-seizoen",
      ],
      en: [
        "Full-funnel setup from day one: Meta, Google Ads and Klaviyo",
        "Creative strategy around the product in use: cooking, outdoor living, season",
        "CRO on the webshop and continuous A/B testing",
        "Seasonal planning aligned with the outdoor cooking season",
      ],
    },
    results: [
      { label: { nl: "ROAS in maand twee", en: "ROAS in month two" }, value: "3,5" },
      { label: { nl: "Orders april \u2192 mei", en: "Orders April \u2192 May" }, value: "4x" },
      { label: { nl: "Vertoningen in Q1 van het seizoen", en: "Impressions in Q1 of the season" }, value: "1 mln+" },
      { label: { nl: "Open rate e-mail", en: "Email open rate" }, value: "44\u201348%" },
    ],
    description: {
      nl: "Moddern is een Nederlands premium kamado-merk, gelanceerd in 2024. Vanaf het eerste seizoen werken we samen aan de volledige funnel: Meta en Google Ads, CRO op de webshop en e-mailflows in Klaviyo.\n\nBij een premium product in een seizoensgebonden markt draait alles om timing en merkbeleving. De creatives laten het product zien zoals het bedoeld is: buiten, in gebruik, met echt eten en echt vuur.\n\nDe opbouw betaalt zich snel uit: de ROAS groeide van 0,67 in de opstartmaand april naar 3,51 in mei, het aantal orders verviervoudigde en e-mail draait met open rates van 44\u201348% ruim boven de benchmark.",
      en: "Moddern is a Dutch premium kamado brand, launched in 2024. From the first season we have been working on the full funnel: Meta and Google Ads, CRO on the webshop and email flows in Klaviyo.\n\nWith a premium product in a seasonal market, everything comes down to timing and brand experience. The creatives show the product as intended: outdoors, in use, with real food and real fire.\n\nThe build-up pays off quickly: ROAS grew from 0.67 in launch month April to 3.51 in May, orders quadrupled, and email runs at open rates of 44\u201348%, well above benchmark.",
    },
    channels: ["Meta Advertising", "Google Ads", "Klaviyo", "CRO", "A/B Testing", "Google Analytics"],
    quote: {
      text: {
        nl: "Vanaf het eerste gesprek was duidelijk dat ze zowel de creatieve als de commerci\u00eble kant van het schalen van een premium merk begrijpen.",
        en: "From the first call, it was clear they understand both the creative and the commercial side of scaling a premium brand.",
      },
      author: "Alrik Kielman",
      image: "/cases/avatars/avatar-alrik.jpg",
      role: "Founder Moddern",
    },
    website: "https://www.moddern.eu/",
    media: [
      { type: "video", src: "/cases/moddern-ad-1.mp4" },
      { type: "video", src: "/cases/moddern-ad-2.mp4" },
    ],
  },
  {
    slug: "vedder-vedder",
    name: "Vedder & Vedder",
    tagline: {
      nl: "Gepersonaliseerde sieraden veroveren Europa",
      en: "Personalized jewelry conquering Europe",
    },
    heroImage: "/cases/vedder-vedder.jpg",
    vertical: "Fashion & Sieraden",
    services: ["Meta Advertising", "TikTok Advertising", "Pinterest Ads", "Google Ads"],
    goals: {
      nl: [
        "Social engagement omzetten in e-commerce omzet",
        "Maximale ROI uit mediabudget behalen",
        "Internationale markten betreden",
      ],
      en: [
        "Convert social engagement into e-commerce revenue",
        "Maximize ROI from media budget",
        "Enter international markets",
      ],
    },
    approach: {
      nl: [
        "Cross-channel strategie: Meta, Google, Pinterest en TikTok",
        "Volledige e-commerce funnel tracking opgezet",
        "Multi-layered retargeting met seizoenscampagnes",
        "Advertentie content A/B testing en optimalisatie",
      ],
      en: [
        "Cross-channel strategy: Meta, Google, Pinterest and TikTok",
        "Full e-commerce funnel tracking setup",
        "Multi-layered retargeting with seasonal campaigns",
        "Ad content A/B testing and optimization",
      ],
    },
    results: [
      { label: { nl: "Omzetgroei", en: "Revenue growth" }, value: "100%" },
      { label: { nl: "Groei per jaar", en: "Growth per year" }, value: "5 jaar" },
      { label: { nl: "Expansie", en: "Expansion" }, value: "UK, DE, FR" },
    ],
    description: {
      nl: "Vedder & Vedder is een Amsterdams sieradenmerk van de tweelingzussen Anne en Esther Vedder. Hun handgemaakte sieraden kun je personaliseren met vingerafdrukken, initialen of een persoonlijke boodschap.\n\nVan 2016 tot 2021 zetten we de liefde voor het merk op social media om in structurele online omzet. We ontdekten dat hun producten populaire cadeaus zijn voor Valentijnsdag, Moederdag en Kerst - de marketingstrategie is hierop afgestemd.\n\nHet resultaat: vijf jaar achter elkaar een verdubbeling van de omzet, en succesvolle internationale expansie naar het Verenigd Koninkrijk, Duitsland en Frankrijk.",
      en: "Vedder & Vedder is an Amsterdam-based jewelry brand by twin sisters Anne and Esther Vedder. Their handmade jewelry can be personalized with fingerprints, initials, or a personal message.\n\nFrom 2016 to 2021, we converted the love for the brand on social media into structural online revenue. We discovered their products are popular gifts for Valentine's Day, Mother's Day, and Christmas - the marketing strategy was aligned accordingly.\n\nThe result: five consecutive years of doubling revenue, and successful international expansion to the UK, Germany, and France.",
    },
    channels: ["Meta Advertising", "TikTok Advertising", "Pinterest Ads", "Google Ads", "A/B Testing", "Databox", "Google Analytics", "Google Tag Manager"],
    quote: {
      text: {
        nl: "Met de hulp van Wrkt zijn we de afgelopen jaren flink gegroeid, van gepassioneerde start-up naar een merk met internationale ambities. Hun online campagnes en conversieoptimalisaties hebben daar een belangrijke rol in gespeeld.",
        en: "With the help of Wrkt, we have grown significantly over the past years, from a passionate start-up to a brand with international ambitions. Their online campaigns and conversion optimizations have played an important role in that.",
      },
      author: "Anne & Esther Vedder",
      image: "/cases/avatars/avatar-vedder.jpg",
      role: "Founders, Vedder & Vedder",
    },
    website: "https://www.vedder-vedder.com/",
    resultsTitle: {
      nl: "Vijf jaar op rij verdubbeld.",
      en: "Doubled five years in a row.",
    },
    timeline: [
      {
        marker: "2016",
        title: { nl: "Start samenwerking", en: "Partnership starts" },
        text: { nl: "Een warme community op social media, klaar om om te zetten in structurele online omzet.", en: "A warm social media community, ready to convert into structural online revenue." },
      },
      {
        marker: "Funnel",
        title: { nl: "Volledige e-commerce funnel", en: "Full e-commerce funnel" },
        text: { nl: "Tracking, cross-channel campagnes en multi-layered retargeting: elke euro meetbaar.", en: "Tracking, cross-channel campaigns and multi-layered retargeting: every euro measurable." },
      },
      {
        marker: "Cadeau",
        title: { nl: "Cadeaumomenten als anker", en: "Gifting moments as anchor" },
        text: { nl: "Valentijnsdag, Moederdag en Kerst werden de ankers van de marketingkalender.", en: "Valentine's Day, Mother's Day and Christmas became the anchors of the marketing calendar." },
      },
      {
        marker: "5 jaar",
        title: { nl: "Elk jaar verdubbeld", en: "Doubled every year" },
        text: { nl: "Vijf jaar achter elkaar een verdubbeling van de omzet.", en: "Five consecutive years of doubling revenue." },
      },
      {
        marker: "EU",
        title: { nl: "Internationale expansie", en: "International expansion" },
        text: { nl: "Dezelfde aanpak, lokale campagnes: het VK, Duitsland en Frankrijk.", en: "Same approach, local campaigns: the UK, Germany and France." },
      },
    ],
    media: [
      { type: "image", src: "/cases/vedder-campagne-1.webp" },
      { type: "image", src: "/cases/vedder-campagne-2.webp" },
    ],
    story: {
      nl: [
        "Vedder & Vedder is een Amsterdams sieradenmerk van de tweelingzussen Anne en Esther Vedder. Hun handgemaakte sieraden kun je personaliseren met vingerafdrukken, initialen of een persoonlijke boodschap. Daarmee verkopen ze iets persoonlijks: een herinnering die je draagt.",
        "Toen we in 2016 begonnen, had het merk al een warme community op social media. De vraag was hoe je die liefde omzet in voorspelbare online omzet, met behoud van de persoonlijke uitstraling die het merk groot maakte.",
        "We bouwden de volledige e-commerce funnel op: tracking, cross-channel campagnes op Meta, Google, Pinterest en TikTok, en multi-layered retargeting. Elke euro mediabudget werd meetbaar en elke campagne stuurde op rendement.",
        "Het grootste inzicht kwam uit de data: gepersonaliseerde sieraden zijn cadeaus. Valentijnsdag, Moederdag en Kerst werden de ankers van de marketingkalender, met seizoenscampagnes die elk jaar scherper werden door doorlopende A/B-tests op creatives en doelgroepen.",
        "Het resultaat: vijf jaar achter elkaar een verdubbeling van de omzet. Die groei gaf ruimte voor de volgende stap: internationale expansie naar het Verenigd Koninkrijk, Duitsland en Frankrijk, met dezelfde bewezen aanpak en lokale campagnes per markt.",
        "Vandaag is Vedder & Vedder uitgegroeid van gepassioneerde start-up tot een merk met internationale ambities. En dat blijven we samen uitbouwen.",
      ],
      en: [
        "Vedder & Vedder is an Amsterdam jewelry brand by twin sisters Anne and Esther Vedder. Their handmade jewelry can be personalized with fingerprints, initials or a personal message. What they sell is personal: a memory you wear.",
        "When we started in 2016, the brand already had a warm community on social media. The question was how to convert that love into predictable online revenue, while keeping the personal character that made the brand big.",
        "We built the full e-commerce funnel: tracking, cross-channel campaigns on Meta, Google, Pinterest and TikTok, and multi-layered retargeting. Every euro of media budget became measurable and every campaign was steered on return.",
        "The biggest insight came from the data: personalized jewelry is a gift. Valentine's Day, Mother's Day and Christmas became the anchors of the marketing calendar, with seasonal campaigns sharpened every year through continuous A/B testing on creatives and audiences.",
        "The result: five consecutive years of doubling revenue. That growth created room for the next step: international expansion to the United Kingdom, Germany and France, with the same proven approach and local campaigns per market.",
        "Today Vedder & Vedder has grown from a passionate start-up into a brand with international ambitions. And together we keep building.",
      ],
    },
  },
  {
    slug: "franky-amsterdam",
    name: "Franky Amsterdam",
    tagline: {
      nl: "Van start-up naar geliefd sieradenmerk in vijf jaar",
      en: "From start-up to beloved jewelry brand in five years",
    },
    heroImage: "/cases/franky-amsterdam.webp",
    vertical: "Fashion & Sieraden",
    services: ["Meta Advertising", "TikTok Advertising", "Google Ads"],
    goals: {
      nl: [
        "Brand awareness en online omzet structureel verhogen",
        "Social Advertising en Google Shopping inzetten",
        "Conversieoptimalisatie doorvoeren",
      ],
      en: [
        "Structurally increase brand awareness and online revenue",
        "Deploy Social Advertising and Google Shopping",
        "Implement conversion optimization",
      ],
    },
    approach: {
      nl: [
        "Doelgroepen getest en geoptimaliseerd per kanaal",
        "TikTok toegevoegd als nieuw advertentiekanaal",
        "Seizoenscampagnes: Valentijnsdag, Black Friday, Kerst",
        "Real-time dashboards voor data-gedreven beslissingen",
      ],
      en: [
        "Tested and optimized audiences per channel",
        "Added TikTok as new advertising channel",
        "Seasonal campaigns: Valentine's Day, Black Friday, Christmas",
        "Real-time dashboards for data-driven decisions",
      ],
    },
    results: [
      { label: { nl: "ROAS Meta & TikTok (YoY)", en: "ROAS Meta & TikTok (YoY)" }, value: "+40%" },
      { label: { nl: "Kosten per aankoop (YoY)", en: "Cost per purchase (YoY)" }, value: "-18%" },
      { label: { nl: "Omzet uit paid (YoY)", en: "Paid revenue (YoY)" }, value: "+18%" },
    ],
    description: {
      nl: "Franky Amsterdam is een Nederlands sieradenmerk met grote plannen. Van 2019 tot 2023 hielpen we hen met social advertising en conversieoptimalisatie.\n\nWat begon met campagnes op Instagram en Facebook is uitgegroeid tot een volledige cross-channel strategie, inclusief TikTok. De omzet verdubbelde ruim, vijf jaar op rij. Het bereik via social ads is enorm en de organische traffic groeit gestaag.\n\nHeldere real-time dashboards maken het mogelijk om alles te volgen en data-gedreven beslissingen te nemen.",
      en: "Franky Amsterdam is a Dutch jewelry brand with big plans. From 2019 to 2023, we helped them with social advertising and conversion optimization.\n\nWhat started with campaigns on Instagram and Facebook has grown into a full cross-channel strategy, including TikTok. Revenue more than doubled, five years in a row. Reach through social ads is massive and organic traffic grows steadily.\n\nClear real-time dashboards make it possible to track everything and make data-driven decisions.",
    },
    channels: ["Meta Advertising", "TikTok Advertising", "Google Ads", "Databox", "Google Analytics", "Google Tag Manager"],
    quote: {
      text: {
        nl: "Ik werk nu een paar jaar samen met Wrkt en ik ben erg positief! Ze helpen niet alleen bij vragen over de advertenties, maar bij veel meer dan dat. Heel fijn!",
        en: "I've been working with Wrkt for a few years now and I'm very positive! They help not only with questions about advertising, but with much more than that. Really great!",
      },
      author: "Franka Vedder",
      image: "/cases/avatars/avatar-franka.jpg",
      role: "Founder, Franky Amsterdam",
    },
    website: "https://www.frankyamsterdam.com/",
    resultsTitle: {
      nl: "Vijf jaar samen gegroeid.",
      en: "Five years of growth together.",
    },
    timeline: [
      { marker: "2019", title: { nl: "Partner vanaf het begin", en: "Partner from the start" }, text: { nl: "Merkbekendheid en traffic: het merk zichtbaar maken bij fashionliefhebbers.", en: "Brand awareness and traffic: making the brand visible to fashion lovers." } },
      { marker: "Groei", title: { nl: "Focus verlegd naar rendement", en: "Focus shifted to return" }, text: { nl: "Met de groei van het merk verschoof de strategie naar omzetgroei en een lagere kost per aankoop, full-funnel.", en: "As the brand grew, strategy shifted to revenue growth and a lower cost per purchase, full-funnel." } },
      { marker: "Creative", title: { nl: "Doorlopende creative-tests", en: "Continuous creative testing" }, text: { nl: "UGC-video's en messaging-tests op Meta en TikTok, plus AI-campagnetypes zoals Advantage+ Shopping.", en: "UGC videos and messaging tests on Meta and TikTok, plus AI campaign types like Advantage+ Shopping." } },
      { marker: "Funnel", title: { nl: "E-mail en leadgeneratie", en: "Email and lead generation" }, text: { nl: "Mid- en bottom-funnel uitgebouwd, met pre-launchcampagnes rond elke collectielancering.", en: "Mid and bottom funnel built out, with pre-launch campaigns around every collection drop." } },
      { marker: "YoY", title: { nl: "Elk jaar scherper", en: "Sharper every year" }, text: { nl: "Jaar op jaar: 18% lagere kosten per aankoop, 18% meer omzet uit paid en 40% hogere ROAS.", en: "Year over year: 18% lower cost per purchase, 18% more paid revenue and 40% higher ROAS." } },
    ],
    story: {
      nl: [
        "Franky Amsterdam is het sieradenmerk van Franka Vedder, de jongere zus van Anne en Esther Vedder. Het merk viert zelfvertrouwen, glamour en vrouwelijkheid met statement-oorbellen die opvallen \u00e9n comfortabel zitten.",
        "Wrkt is sinds het prille begin in 2019 de online marketingpartner. In de beginfase draaide alles om merkbekendheid en traffic: het merk moest gezien worden door de juiste fashionliefhebbers, met een sterke community als fundament.",
        "Door de explosieve groei van het merk, veranderingen binnen digitale platforms en toenemende concurrentie hebben we de strategie herzien: de focus verschoof naar omzetgroei en het verlagen van de gemiddelde kost per aankoop, met een full-funnel e-commerce strategie.",
        "We testen doorlopend creatives en messaging op Meta en TikTok, waaronder UGC-video's, en zetten AI-gedreven campagnetypes in zoals Advantage+ Shopping en Instant Experience. E-mailmarketing en leadgeneratie bouwden we uit als mid- en bottom-funnel kanalen, en met pre-launchcampagnes blijft het merk top of mind bij elke collectielancering.",
        "Het resultaat, jaar op jaar: 18% lagere kosten per aankoop, 18% meer omzet uit betaalde kanalen en een ROAS-toename van 40% op Meta en TikTok.",
      ],
      en: [
        "Franky Amsterdam is the jewelry brand of Franka Vedder, the younger sister of Anne and Esther Vedder. The brand celebrates confidence, glamour and femininity with statement earrings that stand out and are comfortable to wear.",
        "Wrkt has been the online marketing partner since the very beginning in 2019. In the early phase everything revolved around brand awareness and traffic: getting the brand seen by the right fashion lovers, with a strong community as the foundation.",
        "With the brand's explosive growth, changing platforms and increasing competition, we revised the strategy: the focus shifted to revenue growth and lowering the average cost per purchase, with a full-funnel e-commerce strategy.",
        "We continuously test creatives and messaging on Meta and TikTok, including UGC videos, and deploy AI-driven campaign types such as Advantage+ Shopping and Instant Experience. We built out email marketing and lead generation as mid and bottom funnel channels, and pre-launch campaigns keep the brand top of mind at every collection launch.",
        "The result, year over year: 18% lower cost per purchase, 18% more revenue from paid channels and a 40% ROAS increase on Meta and TikTok.",
      ],
    },
  },
  {
    slug: "feather-women",
    name: "Feather Women",
    tagline: {
      nl: "Van nul naar ROAS 7+ in vijf maanden",
      en: "From zero to ROAS 7+ in five months",
    },
    heroImage: "/cases/feather-women.webp",
    vertical: "Beauty & Verzorging",
    services: ["Meta Advertising", "Google Ads", "Klaviyo"],
    goals: {
      nl: [
        "Sterke D2C lancering realiseren",
        "ROAS structureel boven de 7 krijgen",
        "E-mailmarketing via Klaviyo opzetten",
      ],
      en: [
        "Achieve a strong D2C launch",
        "Get ROAS structurally above 7",
        "Set up email marketing via Klaviyo",
      ],
    },
    approach: {
      nl: [
        "Paid advertising strategie voor META en Google Ads",
        "Volledige e-commerce funnel gemeten en geoptimaliseerd",
        "Content, doelgroepen en campagnedoelen continu getest",
        "Klaviyo e-mailstrategie voor retentie",
      ],
      en: [
        "Paid advertising strategy for META and Google Ads",
        "Measured and optimized the full e-commerce funnel",
        "Continuously tested content, audiences, and campaign objectives",
        "Klaviyo email strategy for retention",
      ],
    },
    results: [
      { label: { nl: "ROAS META", en: "ROAS META" }, value: "7+" },
      { label: { nl: "Omzetgroei", en: "Revenue growth" }, value: "90%+" },
      { label: { nl: "Tijd tot resultaat", en: "Time to results" }, value: "5 mnd" },
    ],
    description: {
      nl: "Feather Women is het merk van Anne en Esther Vedder - de oprichters van Vedder & Vedder. Tijdens de zwangerschap van Esther ontdekten zij een gat in de markt: producten voor moderne moeders die wél stijlvol en persoonlijk zijn.\n\nAnne en Esther staan niet voor niets in de Forbes 40 under 40 lijst van 2023. Feather Women maakt sinds de lancering een razendsnelle groei door.\n\nBinnen vijf maanden realiseerden we een ROAS boven de 7 op META en meer dan 90% omzetgroei.",
      en: "Feather Women is the brand by Anne and Esther Vedder - the founders of Vedder & Vedder. During Esther's pregnancy, they discovered a gap in the market: products for modern mothers that are stylish and personal.\n\nAnne and Esther made the Forbes 40 under 40 list in 2023 for good reason. Feather Women has experienced rapid growth since launch.\n\nWithin five months, we achieved a ROAS above 7 on META and more than 90% revenue growth.",
    },
    channels: ["Meta Advertising", "Google Ads", "Klaviyo", "Databox", "Google Analytics", "Google Tag Manager"],
    quote: {
      text: {
        nl: "Toen we met een nieuwe start-up snel wilden groeien, was Wrkt Digital de logische keuze na het succes dat we samen met Vedder & Vedder hebben neergezet. Ze zijn niet alleen sterk in het opschalen van een start-up: ze gaan verder dan adverteren alleen, met forecasts, breed strategisch advies en een proactieve drive voor het beste resultaat.",
        en: "When we wanted to grow a new start-up fast, Wrkt Digital was the obvious choice after the success we created together with Vedder & Vedder. They're not only good at scaling a start-up: they go beyond advertising alone, with forecasts, broad strategic advice and a proactive drive for the best results.",
      },
      author: "Anne & Esther Vedder",
      image: "/cases/avatars/avatar-vedder.jpg",
      role: "Founders, Feather Women",
    },
    website: "https://featherwomen.com/",
    resultsTitle: {
      nl: "Van nul naar ROAS 7+.",
      en: "From zero to ROAS 7+.",
    },
    timeline: [
      { marker: "2016\u20132021", title: { nl: "Vedder & Vedder", en: "Vedder & Vedder" }, text: { nl: "Vijf jaar samenwerking maakte van de start-up een van de bekendste sieradenmerken van Nederland.", en: "Five years of collaboration turned the start-up into one of the best-known jewelry brands in the Netherlands." } },
      { marker: "Start", title: { nl: "Feather Women gelanceerd", en: "Feather Women launched" }, text: { nl: "Anne en Esther vroegen Wrkt opnieuw: bouw de online advertisingstrategie voor het nieuwe merk.", en: "Anne and Esther asked Wrkt again: build the online advertising strategy for the new brand." } },
      { marker: "Setup", title: { nl: "Funnel vanaf dag \u00e9\u00e9n meetbaar", en: "Funnel measurable from day one" }, text: { nl: "Cross-channel campagnes op Meta en Google Ads, leadverzameling, Klaviyo en realtime dashboards.", en: "Cross-channel campaigns on Meta and Google Ads, lead collection, Klaviyo and realtime dashboards." } },
      { marker: "5 mnd", title: { nl: "Doelstellingen overtroffen", en: "Targets exceeded" }, text: { nl: "ROAS op Meta boven de 7 en meer dan 90% omzetgroei binnen vijf maanden.", en: "ROAS on Meta above 7 and over 90% revenue growth within five months." } },
    ],
    story: {
      nl: [
        "Feather Women is het tweede merk van Anne en Esther Vedder. Tijdens Esthers zwangerschap zagen de zussen ruimte voor een persoonlijker merk voor aanstaande moeders: een merk dat producten van hoge kwaliteit levert en moderne moeders verbindt, inspireert en empowert.",
        "Van 2016 tot en met 2021 werkten Wrkt en Vedder & Vedder samen om die start-up te transformeren tot een van de snelst groeiende sieradenmerken van Nederland. Voor Feather Women vroegen Anne en Esther ons opnieuw om de online advertisingstrategie te ontwikkelen en uit te voeren.",
        "We bouwden de paid-strategie vanaf nul op: de volledige e-commerce funnel meetbaar, cross-channel campagnes op Meta en Google Ads, doorlopende tests van content, campagnedoelstellingen en doelgroepen, leadverzameling en ondersteuning van de Klaviyo-strategie. Alles zichtbaar in realtime dashboards.",
        "Binnen vijf maanden na de start groeide de ROAS op Meta naar boven de 7 en steeg de omzet met meer dan 90%. Feather Women overtrof daarmee alle doelstellingen.",
        "Anne en Esther stonden in 2023 in de Forbes 40 under 40. Inmiddels zijn beide zussen trotse moeders \u00e9n bouwen ze aan hun tweede snelgroeiende merk.",
      ],
      en: [
        "Feather Women is the second brand of Anne and Esther Vedder. During Esther's pregnancy, the sisters saw room for a more personal brand for expecting mothers: a brand that delivers high-quality products and connects, inspires and empowers modern mothers.",
        "From 2016 through 2021, Wrkt and Vedder & Vedder worked together to transform that start-up into one of the fastest-growing jewelry brands in the Netherlands. For Feather Women, Anne and Esther asked us again to develop and execute the online advertising strategy.",
        "We built the paid strategy from scratch: the full e-commerce funnel measurable, cross-channel campaigns on Meta and Google Ads, continuous testing of content, campaign objectives and audiences, lead collection and support of the Klaviyo strategy. All visible in realtime dashboards.",
        "Within five months of launch, ROAS on Meta grew above 7 and revenue increased by more than 90%. Feather Women exceeded all its targets.",
        "Anne and Esther were listed in the Forbes 40 under 40 in 2023. Both sisters are now proud mothers, building their second fast-growing brand.",
      ],
    },
  },
  {
    slug: "zevy",
    name: "Zèvy",
    tagline: {
      nl: "Premium wasparfum: van lancering naar marktleider",
      en: "Premium laundry perfume: from launch to market leader",
    },
    heroImage: "/cases/zevy.jpg",
    vertical: "Home & Living",
    services: ["Meta Advertising", "TikTok Advertising", "Google Ads", "Klaviyo"],
    goals: {
      nl: [
        "Snel klantvolume opbouwen voor marktpenetratie",
        "Brand awareness creëren via TikTok, Instagram en Facebook",
        "Herhaalaankopen stimuleren via Klaviyo",
      ],
      en: [
        "Rapidly build customer volume for market penetration",
        "Create brand awareness via TikTok, Instagram, and Facebook",
        "Stimulate repeat purchases via Klaviyo",
      ],
    },
    approach: {
      nl: [
        "Complete cross-channel strategie: META, TikTok en Google",
        "Optimale doelgroep geïdentificeerd via testing (vrouwen 25+)",
        "UGC video's als best presterende content ingezet",
        "Klaviyo flows voor maximale retentie en herhaalaankopen",
      ],
      en: [
        "Complete cross-channel strategy: META, TikTok, and Google",
        "Identified optimal audience through testing (women 25+)",
        "Deployed UGC videos as best-performing content",
        "Klaviyo flows for maximum retention and repeat purchases",
      ],
    },
    results: [
      { label: { nl: "Omzetgroei in 3 maanden", en: "Revenue growth in 3 months" }, value: "80%" },
      { label: { nl: "Mensen bereikt", en: "People reached" }, value: "1M+" },
      { label: { nl: "Herhaalaankopen", en: "Repeat purchases" }, value: "Stijgend" },
    ],
    description: {
      nl: "Zèvy creëert exclusieve wasparfums met 100% eau de parfum - ontwikkeld met 's werelds beste parfumhuis. Geen wasverzachter, maar luxe geur voor je garderobe.\n\nOm Zèvy te introduceren startten we campagnes op TikTok, Instagram en Facebook. Al snel bleek dat UGC video's uitstekend presteren bij vrouwen ouder dan 25. Google Ads en Klaviyo completeren de funnel.\n\nHet klantvolume neemt snel toe, het e-mailbestand groeit en de wasparfums van Zèvy veroveren een vaste plek in Nederlandse huishoudens.",
      en: "Zèvy creates exclusive laundry perfumes with 100% eau de parfum - developed with the world's finest perfume house. Not fabric softener, but luxury fragrance for your wardrobe.\n\nTo introduce Zèvy, we launched campaigns on TikTok, Instagram, and Facebook. UGC videos quickly proved to perform exceptionally well with women aged 25+. Google Ads and Klaviyo complete the funnel.\n\nThe customer base is growing rapidly, the email list is expanding, and Zèvy's perfumes are securing a permanent place in Dutch households.",
    },
    channels: ["Meta Advertising", "TikTok Advertising", "Google Ads", "Klaviyo", "Databox", "Google Analytics", "Google Tag Manager"],
    quote: {
      text: {
        nl: "Met de advertising- en e-mailstrategie van Wrkt Digital zijn we de afgelopen maanden aanzienlijk gegroeid. Ze hanteren een unieke aanpak waarbij ze meerdere paid kanalen inzetten en constant optimaliseren. Ze geven daarnaast realtime inzicht in de resultaten via gave dashboards!",
        en: "With Wrkt Digital's advertising and email strategy, we've grown significantly over the past months. They use a unique approach deploying multiple paid channels and constantly optimizing. They also provide real-time insight into results via amazing dashboards!",
      },
      author: "Tomas Breemen",
      role: "Eigenaar, Zèvy",
    },
  },
  {
    slug: "amaya-amsterdam",
    name: "Amaya Amsterdam & Studio Amaya",
    tagline: {
      nl: "Twee merken, één strategie, dubbele groei",
      en: "Two brands, one strategy, double the growth",
    },
    heroImage: "/cases/amaya-amsterdam.jpeg",
    vertical: "Fashion & Sieraden",
    services: ["Meta Advertising", "TikTok Advertising", "Google Ads"],
    goals: {
      nl: [
        "Groeistrategie voor twee e-commerce merken tegelijk",
        "Nieuw dochtermerk lanceren bij jongere doelgroep",
        "Online-to-offline crossover groei realiseren",
      ],
      en: [
        "Growth strategy for two e-commerce brands simultaneously",
        "Launch new sub-brand with younger target audience",
        "Achieve online-to-offline crossover growth",
      ],
    },
    approach: {
      nl: [
        "Separate campagnestrategie per merk ontwikkeld",
        "Ideale doelgroep per merk geïdentificeerd via testing",
        "META, TikTok en Google Ads cross-channel ingezet",
        "Seizoenscollecties met warme retargeting audiences",
      ],
      en: [
        "Developed separate campaign strategy per brand",
        "Identified ideal target audience per brand through testing",
        "Deployed META, TikTok, and Google Ads cross-channel",
        "Seasonal collections with warm retargeting audiences",
      ],
    },
    results: [
      { label: { nl: "Omzetgroei 2023", en: "Revenue growth 2023" }, value: "3x" },
      { label: { nl: "Vrouwen bereikt", en: "Women reached" }, value: "1.3M" },
      { label: { nl: "Retail verkooppunten", en: "Retail locations" }, value: "Groeiend" },
    ],
    description: {
      nl: "Amaya Amsterdam en Studio Amaya zijn fashionmerken die vrouwen de mogelijkheid bieden om eindeloos te combineren. Met bijpassende tops en bottoms heb je in één klap een perfect gematchte outfit.\n\nVoor de lancering van het online-only dochtermerk Studio Amaya moest een nieuwe, jongere doelgroep worden aangeboord. Door doelgroeptests op social media vonden we de juiste targeting per merk.\n\nStudio Amaya verdubbelde de omzet in het tweede jaar. Amaya Amsterdam zag naast online groei ook een duidelijke stijging in retail verkooppunten - een sterk voorbeeld van online-to-offline crossover groei.",
      en: "Amaya Amsterdam and Studio Amaya are fashion brands that offer women the ability to mix and match endlessly. With matching tops and bottoms, you instantly have a perfectly coordinated outfit.\n\nFor the launch of the online-only sub-brand Studio Amaya, a new, younger target audience needed to be reached. Through audience testing on social media, we found the right targeting per brand.\n\nStudio Amaya doubled its revenue in the second year. Amaya Amsterdam saw clear growth in retail locations alongside online growth - a strong example of online-to-offline crossover growth.",
    },
    channels: ["Meta Advertising", "TikTok Advertising", "Google Ads", "Databox", "Google Analytics", "Google Tag Manager"],
    quote: {
      text: {
        nl: "Sinds 2021 werken wij met Wrkt Digital samen voor Amaya Amsterdam en Studio Amaya. Door de juiste targeting, budgettering en strategie verdubbelen we ieder jaar weer onze omzet en bereik. Er is iedere week nauw contact - dat maakt het succes. Keep up the good work!",
        en: "Since 2021, we've been working with Wrkt Digital for Amaya Amsterdam and Studio Amaya. Through the right targeting, budgeting, and strategy, we double our revenue and reach every year. There is close contact every week - that's what makes it successful. Keep up the good work!",
      },
      author: "Pien Ernst",
      image: "/cases/avatars/avatar-pien.jpg",
      role: "Eigenaar, Amaya Amsterdam & Studio Amaya",
    },
    website: "https://www.amaya-amsterdam.com/",
    resultsTitle: {
      nl: "Elke drop een campagne.",
      en: "Every drop a campaign.",
    },
    timeline: [
      { marker: "2021", title: { nl: "Lancering Studio Amaya", en: "Studio Amaya launch" }, text: { nl: "Na het succes van Amaya Amsterdam hielpen we Pien Ernst met de lancering van haar tweede merk.", en: "After the success of Amaya Amsterdam we helped Pien Ernst launch her second brand." } },
      { marker: "Funnel", title: { nl: "Full-funnel op drie kanalen", en: "Full-funnel on three channels" }, text: { nl: "Meta, TikTok en Google Ads vanuit \u00e9\u00e9n strategie, gestuurd op ROAS en kost per aankoop.", en: "Meta, TikTok and Google Ads from one strategy, steered on ROAS and cost per purchase." } },
      { marker: "Drops", title: { nl: "Leadgen v\u00f3\u00f3r elke drop", en: "Lead gen before every drop" }, text: { nl: "Vroege toegang tot de collectie bouwt een e-maillijst die de kosten van paid verlaagt.", en: "Early access to the collection builds an email list that lowers paid costs." } },
      { marker: "Okt 2023", title: { nl: "Winterdrop", en: "Winter drop" }, text: { nl: "Binnen drie maanden 67% meer aankopen uit paid en ruim 40% hogere ROAS op Meta en TikTok.", en: "Within three months 67% more paid purchases and over 40% higher ROAS on Meta and TikTok." } },
      { marker: "2023", title: { nl: "Omzet bijna verdrievoudigd", en: "Revenue nearly tripled" }, text: { nl: "In 2023 groeide de omzet naar bijna 3x het jaar ervoor, met een piek rond de winterdrop.", en: "In 2023 revenue grew to almost 3x the year before, peaking around the winter drop." } },
    ],
    story: {
      nl: [
        "Studio Amaya is het Amsterdamse damesmodemerk van Pien Ernst, actief in Nederland en Belgi\u00eb, ontstaan na het succes van Amaya Amsterdam. Een eigen positionering, prijspunt en doelgroep, met collecties die eindeloos te mixen en matchen zijn.",
        "In 2021 hielpen we bij de lancering van Studio Amaya. Tot begin 2024 draaide de samenwerking om een full-funnel strategie op Meta, TikTok en Google Ads: nieuwe klanten bereiken, kwalitatieve aankopen binnenhalen en een gezonde verhouding tussen ROAS en kost per aankoop bewaken.",
        "Het ritme van het merk bepaalt de strategie: elke collectiedrop is een campagne. V\u00f3\u00f3r de lancering bouwen we met leadgeneratie een lijst van liefhebbers die vroege toegang krijgen. Die e-maillijst verlaagt de kosten van betaalde kanalen bij elke volgende drop en laat vooraf zien welke creatives het beste werken.",
        "Op lanceringsdag gaan conversiecampagnes live voor koude en warme doelgroepen, met vooraf geteste creatives, UGC-video's en dynamische catalogusadvertenties. Wat presteert, schalen we op.",
        "De winterdrop van oktober 2023 leverde binnen drie maanden 67% meer aankopen uit betaalde kanalen en ruim 40% hogere ROAS op Meta en TikTok. Over heel 2023 groeide de omzet naar bijna drie keer het jaar ervoor.",
      ],
      en: [
        "Studio Amaya is the Amsterdam women's fashion brand of Pien Ernst, active in the Netherlands and Belgium, born after the success of Amaya Amsterdam. Its own positioning, price point and audience, with collections made for endless mix and match.",
        "In 2021 we helped launch Studio Amaya. Until early 2024 the partnership revolved around a full-funnel strategy on Meta, TikTok and Google Ads: reaching new customers, acquiring high-quality purchases and guarding a healthy balance between ROAS and cost per purchase.",
        "The brand's rhythm shapes the strategy: every collection drop is a campaign. Before launch, we build a list of brand lovers through lead generation with early access. That email list lowers paid costs with every next drop and shows in advance which creatives work best.",
        "On launch day, conversion campaigns go live for cold and warm audiences, with pre-tested creatives, UGC videos and dynamic catalog ads. What performs, we scale.",
        "The October 2023 winter drop delivered 67% more paid purchases and over 40% higher ROAS on Meta and TikTok within three months. Across 2023, revenue grew to almost three times the year before.",
      ],
    },
  },
  {
    slug: "koi-atelier",
    name: "Koi Atelier",
    tagline: {
      nl: "Handgemaakte sieraden met een persoonlijk verhaal",
      en: "Handmade jewelry with a personal story",
    },
    heroImage: "/cases/koi-atelier.jpg",
    vertical: "Fashion & Sieraden",
    services: ["Meta Advertising"],
    goals: {
      nl: [
        "Naamsbekendheid vergroten buiten het Amsterdamse atelier",
        "Websiteverkeer en videoweergaven verhogen",
        "Conversieratio naar aankopen verbeteren",
      ],
      en: [
        "Increase brand awareness beyond the Amsterdam studio",
        "Boost website traffic and video views",
        "Improve conversion rate to purchases",
      ],
    },
    approach: {
      nl: [
        "Doelgroeponderzoek met bestaande klantdata",
        "Meta advertentiestrategie gericht op bereik en traffic",
        "Mediabudget geoptimaliseerd op campagneniveau",
        "Content testing: video vs. statisch, storytelling vs. product",
      ],
      en: [
        "Audience research with existing customer data",
        "Meta advertising strategy focused on reach and traffic",
        "Optimized media budget at campaign level",
        "Content testing: video vs. static, storytelling vs. product",
      ],
    },
    results: [
      { label: { nl: "Mensen bereikt", en: "People reached" }, value: "100K+" },
      { label: { nl: "Videoweergaven", en: "Video views" }, value: "250K+" },
      { label: { nl: "Websiteverkeer", en: "Website traffic" }, value: "3x" },
    ],
    description: {
      nl: "Koi Atelier is opgericht door Roxy Lunn in 2015. Vanuit haar atelier in het hart van Amsterdam zet ze persoonlijke verhalen om in prachtige handgemaakte sieraden - tijdloos, duurzaam en toegankelijk.\n\nSinds begin 2024 laten we het merk groeien via META advertising. Door te testen met verschillende content formats ontdekten we dat storytelling video's het beste aanslaan bij de doelgroep.\n\nMet een gericht mediabudget bereiken we meer dan 100.000 mensen per maand en is het websiteverkeer verdrievoudigd.",
      en: "Koi Atelier was founded by Roxy Lunn in 2015. From her studio in the heart of Amsterdam, she transforms personal stories into beautiful handmade jewelry - timeless, sustainable, and accessible.\n\nSince early 2024, we've been growing the brand through META advertising. By testing different content formats, we discovered that storytelling videos resonate best with the target audience.\n\nWith a focused media budget, we reach over 100,000 people per month and website traffic has tripled.",
    },
    channels: ["Meta Advertising", "Google Analytics", "Google Tag Manager", "Microsoft Clarity"],
    website: "https://koiatelier.com/",
  },
  {
    slug: "pig-hen",
    name: "Pig & Hen",
    tagline: {
      nl: "Van Amsterdams atelier naar 5 landen",
      en: "From Amsterdam studio to 5 countries",
    },
    heroImage: "/cases/pig-hen.webp",
    vertical: "Fashion & Sieraden",
    services: ["Meta Advertising", "Google Ads"],
    goals: {
      nl: [
        "Webshop positioneren als substantiële omzetbron",
        "Beste advertentiekanalen voor mannenpubliek vinden",
        "Internationale expansie realiseren",
      ],
      en: [
        "Position webshop as substantial revenue source",
        "Find best advertising channels for male audience",
        "Achieve international expansion",
      ],
    },
    approach: {
      nl: [
        "E-commerce funnel analyse en checkout optimalisatie",
        "Doelgroeptesting per land (NL, BE, DE, FR, UK)",
        "Content A/B testing voor mannelijk publiek",
        "Hotjar en Analytics voor gebruikersgedrag-analyse",
      ],
      en: [
        "E-commerce funnel analysis and checkout optimization",
        "Audience testing per country (NL, BE, DE, FR, UK)",
        "Content A/B testing for male audience",
        "Hotjar and Analytics for user behavior analysis",
      ],
    },
    results: [
      { value: "792%", label: { nl: "Omzetgroei in 3 jaar", en: "Revenue growth in 3 years" } },
      { value: "5", label: { nl: "Landen actief", en: "Countries active" } },
      { value: "+26%", label: { nl: "Conversieratio", en: "Conversion rate" } },
    ],
    description: {
      nl: "Pig&Hen maakt stoere armbanden van echt scheepstouw, geïnspireerd door de maritieme historie van Nederland. Het merk begon in 2013 met offline retail en lanceerde een jaar later de webshop.\n\nSinds eind 2015 helpen we met online marketing. Door de online omzet twee jaar achter elkaar te verdrievoudigen, namen ook de offline retail sales enorm toe. Een nieuwe checkout leverde 26% meer conversies op.\n\nPig&Hen is nu een schoolvoorbeeld van succesvolle e-commerce met internationale aanwezigheid in België, Duitsland, Frankrijk en het Verenigd Koninkrijk.",
      en: "Pig&Hen makes rugged bracelets from real ship rope, inspired by the Netherlands' maritime history. The brand started in 2013 with offline retail and launched the webshop a year later.\n\nSince late 2015, we've been helping with online marketing. By tripling online revenue two years in a row, offline retail sales also increased significantly. A new checkout delivered 26% more conversions.\n\nPig&Hen is now a textbook example of successful e-commerce with international presence in Belgium, Germany, France, and the UK.",
    },
    channels: ["Meta Advertising", "Google Ads", "Google Analytics", "Google Tag Manager", "Hotjar"],
    website: "https://www.pigandhen.com/",
  },
  {
    slug: "melanie-pigeaud",
    name: "Mélanie Pigeaud",
    tagline: {
      nl: "Duurzame sieraden van atelier naar online",
      en: "Sustainable jewelry from studio to online",
    },
    heroImage: "/cases/melanie-pigeaud.webp",
    vertical: "Fashion & Sieraden",
    services: ["Meta Advertising", "Google Ads"],
    goals: {
      nl: [
        "Online clientèle uitbreiden naast fysiek atelier",
        "Juiste doelgroep vinden voor premium segment",
        "Maximaal resultaat binnen beschikbaar budget",
      ],
      en: [
        "Expand online clientele alongside physical studio",
        "Find the right audience for premium segment",
        "Maximum results within available budget",
      ],
    },
    approach: {
      nl: [
        "Doelgroeptesting gericht op premium sieradenmarkt",
        "Social media en Google Ads gecombineerd",
        "Content getest: atelier behind-the-scenes vs. product shots",
        "Websiteconversie geanalyseerd en verbeterd",
      ],
      en: [
        "Audience testing focused on premium jewelry market",
        "Combined social media and Google Ads",
        "Content tested: studio behind-the-scenes vs. product shots",
        "Analyzed and improved website conversion",
      ],
    },
    results: [
      { value: "2x", label: { nl: "Online omzet", en: "Online revenue" } },
      { value: "500K+", label: { nl: "Vrouwen bereikt", en: "Women reached" } },
      { value: "Lager", label: { nl: "Cost per acquisition", en: "Cost per acquisition" } },
    ],
    description: {
      nl: "Mélanie Pigeaud ontwerpt duurzame, minimalistische sieraden van gerecycled goud en zilver. Elk stuk is met de hand gemaakt in haar atelier aan de Amsterdamse grachten.\n\nHaar collecties variëren van exclusieve stukken tot betaalbare opties. Mélanie verwelkomt al jaren klanten in haar atelier, maar wilde haar bereik online uitbreiden naar een breder publiek.\n\nMet social media advertising en Google Ads brengen we haar unieke sieraden bij de juiste doelgroep. De online omzet is verdubbeld en we bereiken meer dan 500.000 vrouwen per jaar.",
      en: "Mélanie Pigeaud designs sustainable, minimalist jewelry from recycled gold and silver. Each piece is handmade in her studio on Amsterdam's canals.\n\nHer collections range from exclusive pieces to affordable options. Mélanie has been welcoming clients to her studio for years, but wanted to extend her reach online to a broader audience.\n\nWith social media advertising and Google Ads, we bring her unique jewelry to the right audience. Online revenue has doubled and we reach over 500,000 women per year.",
    },
    channels: ["Meta Advertising", "Google Ads", "Google Analytics", "Google Tag Manager"],
    website: "https://melaniepigeaud.com/",
  },
  {
    slug: "the-boyscouts",
    name: "The Boyscouts",
    tagline: {
      nl: "Niche sieradenmerk met loyale klantenkring",
      en: "Niche jewelry brand with loyal customer base",
    },
    heroImage: "/cases/the-boyscouts.webp",
    vertical: "Fashion & Sieraden",
    services: ["Meta Advertising"],
    goals: {
      nl: [
        "De juiste niche-doelgroep vinden voor een eigenzinnig merk",
        "Eerste aankopen stimuleren via social advertising",
        "Klantretentie versterken",
      ],
      en: [
        "Find the right niche audience for a distinctive brand",
        "Stimulate first purchases via social advertising",
        "Strengthen customer retention",
      ],
    },
    approach: {
      nl: [
        "Doelgroeptesting voor niche markt",
        "Social advertising gericht op merkbekendheid en traffic",
        "Content strategie afgestemd op de eigenzinnige merkidentiteit",
        "Websiteconversie geanalyseerd en verbeterd",
      ],
      en: [
        "Audience testing for niche market",
        "Social advertising focused on brand awareness and traffic",
        "Content strategy aligned with the distinctive brand identity",
        "Analyzed and improved website conversion",
      ],
    },
    results: [
      { value: "Sterk", label: { nl: "Klantretentie", en: "Customer retention" } },
      { value: "Niche", label: { nl: "Doelgroep gevonden", en: "Audience found" } },
      { value: "Groei", label: { nl: "Online omzet", en: "Online revenue" } },
    ],
    description: {
      nl: "The Boyscouts is een Rotterdams sieradenmerk dat het anders doet. Geen vluchtige trends, maar producten die de tand des tijds doorstaan. Een sieraad als trofee - iets om altijd trots op te zijn.\n\nDie eigenzinnige visie vraagt om een unieke aanpak. De stijl is niet voor iedereen, waardoor het vinden van de juiste doelgroep essentieel is. Na een eerste aankoop komen klanten bijna altijd terug.\n\nWe hebben de ideale niche-doelgroep gevonden via gerichte social advertising, wat leidt tot een hoge klantretentie en groeiende online omzet.",
      en: "The Boyscouts is a Rotterdam jewelry brand that does things differently. No fleeting trends, but products that stand the test of time. Jewelry as a trophy - something to always be proud of.\n\nThis distinctive vision requires a unique approach. The style isn't for everyone, making finding the right audience essential. After a first purchase, customers almost always come back.\n\nWe found the ideal niche audience through targeted social advertising, leading to high customer retention and growing online revenue.",
    },
    channels: ["Meta Advertising", "Google Analytics", "Google Tag Manager"],
    website: "https://theboyscouts.com/",
  },
  {
    slug: "a-la-collection",
    name: "À La Collection",
    tagline: {
      nl: "Handgemaakt in India, geliefd in Nederland",
      en: "Handmade in India, loved in the Netherlands",
    },
    heroImage: "/cases/a-la-collection.jpg",
    vertical: "Fashion & Sieraden",
    services: ["Meta Advertising", "TikTok Advertising", "Google Ads", "Klaviyo"],
    goals: {
      nl: [
        "Online B2C omzet laten groeien",
        "Het merkverhaal inzetten als conversiemiddel",
        "Naamsbekendheid en traffic verhogen",
      ],
      en: [
        "Grow online B2C revenue",
        "Leverage the brand story as a conversion tool",
        "Increase brand awareness and traffic",
      ],
    },
    approach: {
      nl: [
        "Merkverhaal centraal in advertentiecontent",
        "Visueel sterke campagnes op META en TikTok",
        "Google Ads voor koopintentie traffic",
        "Klaviyo voor klantretentie en herhaalaankopen",
      ],
      en: [
        "Brand story central in advertising content",
        "Visually strong campaigns on META and TikTok",
        "Google Ads for purchase intent traffic",
        "Klaviyo for customer retention and repeat purchases",
      ],
    },
    results: [
      { value: "5 jaar", label: { nl: "Samenwerking", en: "Partnership" } },
      { value: "Sterk", label: { nl: "B2C groei", en: "B2C growth" } },
      { value: "Hoog", label: { nl: "Content engagement", en: "Content engagement" } },
    ],
    description: {
      nl: "À La Collection begon in 2013 toen twee ondernemers in India geraakt werden door lokaal vakmanschap. Samen met lokale vakmensen creëren ze sindsdien unieke sieraden en interieurstukken.\n\nHet sterke merkverhaal en de prachtige content maken social media advertising bijzonder effectief. Het verhaal achter elk product resoneert bij de doelgroep en zorgt voor hoge engagement.\n\nVijf jaar lang hebben we À La Collection geholpen om online B2C snel te groeien via META, TikTok, Google Ads en Klaviyo.",
      en: "À La Collection started in 2013 when two entrepreneurs in India were captivated by local craftsmanship. Together with local artisans, they've been creating unique jewelry and interior pieces ever since.\n\nThe strong brand story and beautiful content make social media advertising exceptionally effective. The story behind each product resonates with the audience and drives high engagement.\n\nFor five years, we helped À La Collection grow online B2C rapidly via META, TikTok, Google Ads, and Klaviyo.",
    },
    channels: ["Meta Advertising", "TikTok Advertising", "Google Ads", "Klaviyo", "Databox", "Google Analytics", "Google Tag Manager"],
    resultsTitle: {
      nl: "Een vliegende start.",
      en: "A flying start.",
    },
    timeline: [
      { marker: "2013", title: { nl: "Het merk ontstaat in India", en: "The brand is born in India" }, text: { nl: "Twee eigenaren, ge\u00efnspireerd door de lokale bevolking, cre\u00ebren samen met lokale vakmannen unieke sieraden en interieurstukken.", en: "Two owners, inspired by the local community, create unique jewelry and interior pieces together with local craftsmen." } },
      { marker: "Start", title: { nl: "Vliegende start met sale-campagne", en: "Flying start with a sale campaign" }, text: { nl: "De eerste campagne rond een bijzondere sale zorgde direct voor flink wat extra omzet en nieuwe klanten.", en: "The first campaign around a special sale immediately delivered extra revenue and new customers." } },
      { marker: "Content", title: { nl: "Verhaal als motor", en: "The story as engine" }, text: { nl: "Social advertising rond sterke merkcontent: de ideale doelgroep vinden, ad content testen en retargeting opzetten.", en: "Social advertising around strong brand content: finding the ideal audience, testing ad content and setting up retargeting." } },
      { marker: "5 jaar", title: { nl: "Structurele samenwerking", en: "Structural partnership" }, text: { nl: "Vijf jaar samen bouwen aan online omzet en naamsbekendheid.", en: "Five years of building online revenue and brand awareness together." } },
    ],
    story: {
      nl: [
        "\u00c0 La Collection draait om de reis die de twee eigenaren in 2013 in India zijn begonnen. Ze raakten ge\u00efnspireerd door de lokale bevolking en wilden hun vele talenten en vakmanschap delen. Samen met lokale vakmannen cre\u00ebren ze unieke sieraden en interieurstukken.",
        "Het merk beschikt over prachtige content rondom de collectie. Social media advertising leent zich uitstekend voor merken die een goed verhaal vertellen en over mooie content beschikken, en precies daar ligt de kracht van \u00c0 La Collection.",
        "We startten de samenwerking met een campagne rondom een bijzondere sale. Die vliegende start zorgde direct voor flink wat extra omzet en nieuwe klanten.",
        "De aanpak daarna: Instagram- en Facebook-advertising, de ideale doelgroep vinden, doorlopend experimenteren met ad content en retargeting-strategie\u00ebn opbouwen. Zo groeit de online omzet en naamsbekendheid structureel mee met het merk.",
      ],
      en: [
        "\u00c0 La Collection revolves around the journey the two owners started in India in 2013. Inspired by the local community, they wanted to share their many talents and craftsmanship. Together with local craftsmen they create unique jewelry and interior pieces.",
        "The brand has beautiful content around its collection. Social media advertising is a perfect fit for brands that tell a good story and have strong content, and that is exactly where \u00c0 La Collection excels.",
        "We started the partnership with a campaign around a special sale. That flying start immediately delivered significant extra revenue and new customers.",
        "The approach since: Instagram and Facebook advertising, finding the ideal audience, continuously experimenting with ad content and building retargeting strategies. This way, online revenue and brand awareness grow structurally with the brand.",
      ],
    },
  },
  {
    slug: "feraggio",
    name: "Feraggio",
    tagline: {
      nl: "De perfecte pump: elegant én comfortabel",
      en: "The perfect pump: elegant and comfortable",
    },
    heroImage: "/cases/feraggio.avif",
    vertical: "Fashion & Sieraden",
    services: ["Meta Advertising", "Google Ads"],
    goals: {
      nl: [
        "E-commerce tracking en tooling opzetten",
        "Online omzet en traffic verhogen",
        "Merk positioneren bij de juiste doelgroep",
      ],
      en: [
        "Set up e-commerce tracking and tooling",
        "Increase online revenue and traffic",
        "Position brand with the right audience",
      ],
    },
    approach: {
      nl: [
        "Volledige tracking en advertising setup",
        "Social media campagnes gericht op vrouwen in doelgroep",
        "Google Ads voor koopintentie",
        "Webshop geanalyseerd en geoptimaliseerd voor conversies",
      ],
      en: [
        "Complete tracking and advertising setup",
        "Social media campaigns targeting women in audience",
        "Google Ads for purchase intent",
        "Analyzed and optimized webshop for conversions",
      ],
    },
    results: [
      { value: "500K", label: { nl: "Vrouwen bereikt", en: "Women reached" } },
      { value: "Best", label: { nl: "Kwartaal ooit (Q4)", en: "Quarter ever (Q4)" } },
      { value: "Fors", label: { nl: "Traffic groei", en: "Traffic growth" } },
    ],
    description: {
      nl: "Feraggio is een Nederlands luxe schoenenmerk dat de perfecte pump heeft gecreëerd - elegant én comfortabel. \"Heel the world\" is hun motto.\n\nWe hebben de volledige e-commerce tracking opgezet en campagnes gelanceerd op META en Google Ads. Met een efficiënt budget bereikten we 500.000 vrouwen in de doelgroep.\n\nDe traffic op de website nam fors toe en Q4 was met afstand het beste kwartaal ooit qua online omzet.",
      en: "Feraggio is a Dutch luxury shoe brand that created the perfect pump - elegant and comfortable. \"Heel the world\" is their motto.\n\nWe set up the full e-commerce tracking and launched campaigns on META and Google Ads. With an efficient budget, we reached 500,000 women in the target audience.\n\nWebsite traffic increased significantly and Q4 was by far their best quarter ever in terms of online revenue.",
    },
    channels: ["Meta Advertising", "Google Ads", "Google Analytics", "Google Tag Manager"],
  },
  {
    slug: "dstinctive",
    name: "dstinctive",
    tagline: {
      nl: "Gepersonaliseerde Italiaanse designsokken",
      en: "Personalized Italian design socks",
    },
    heroImage: "/cases/dstinctive.jpg",
    vertical: "Fashion & Sieraden",
    services: ["Meta Advertising", "Pinterest Ads", "Google Ads"],
    goals: {
      nl: [
        "Testen of het personalisatieconcept potentie heeft",
        "Juiste doelgroep ontdekken voor een nieuw productconcept",
        "Herhaalaankopen en cadeauverkoop stimuleren",
      ],
      en: [
        "Test whether the personalization concept has potential",
        "Discover the right audience for a new product concept",
        "Stimulate repeat purchases and gift sales",
      ],
    },
    approach: {
      nl: [
        "Concept validatie via social media campagnes",
        "Interesse-doelgroepen getest op META en Pinterest",
        "Cadeau-targeting rondom Kerst en Vaderdag",
        "Google Ads voor koopintentie sokken en personalisatie",
      ],
      en: [
        "Concept validation through social media campaigns",
        "Interest audiences tested on META and Pinterest",
        "Gift targeting around Christmas and Father's Day",
        "Google Ads for purchase intent socks and personalization",
      ],
    },
    results: [
      { value: "Bewezen", label: { nl: "Concept potentie", en: "Concept potential" } },
      { value: "Vrouwen", label: { nl: "Top doelgroep (cadeaus)", en: "Top audience (gifts)" } },
      { value: "Kerst & VD", label: { nl: "Piek periodes", en: "Peak periods" } },
    ],
    description: {
      nl: "dstinctive biedt mannen de mogelijkheid om Italiaanse designsokken te personaliseren met hun naam of initialen. De sokken worden duurzaam en zero-waste geproduceerd in Italië.\n\nNiet alleen het merk was nieuw, maar ook het hele concept. We testten verschillende interesse-doelgroepen op social media en Pinterest om de markt te valideren.\n\nEen verrassende ontdekking: vrouwen zijn een minstens zo interessante doelgroep - gepersonaliseerde sokken zijn een populair cadeau, vooral rond Kerst en Vaderdag.",
      en: "dstinctive offers men the ability to personalize Italian design socks with their name or initials. The socks are sustainably produced with zero waste in Italy.\n\nNot only was the brand new, but so was the entire concept. We tested different interest audiences on social media and Pinterest to validate the market.\n\nA surprising discovery: women are an equally interesting audience - personalized socks are a popular gift, especially around Christmas and Father's Day.",
    },
    channels: ["Meta Advertising", "Pinterest Ads", "Google Ads", "Google Analytics", "Google Tag Manager"],
    website: "https://dstinctive.com/",
  },
  {
    slug: "brinckers",
    name: "Brinckers",
    tagline: {
      nl: "Strategisch marketingplan voor nieuw fietsenmerk",
      en: "Strategic marketing plan for new bicycle brand",
    },
    heroImage: "/cases/brinckers.jpg",
    vertical: "Mobiliteit",
    services: ["Meta Advertising", "Google Ads"],
    goals: {
      nl: [
        "Online strategie ontwikkelen voor private label lancering",
        "Business case met omzetprognose en ROI opleveren",
        "Social ads en SEA roadmap opzetten",
      ],
      en: [
        "Develop online strategy for private label launch",
        "Deliver business case with revenue forecast and ROI",
        "Set up social ads and SEA roadmap",
      ],
    },
    approach: {
      nl: [
        "Marktanalyse Nederlandse fietsenmarkt",
        "Strategiesessies en analyse huidige resultaten",
        "Strategisch plan met prognose en business case",
        "Uitgebreid keyword onderzoek en mediaplanning",
      ],
      en: [
        "Market analysis of the Dutch bicycle market",
        "Strategy sessions and analysis of current results",
        "Strategic plan with forecast and business case",
        "Extensive keyword research and media planning",
      ],
    },
    results: [
      { value: "Compleet", label: { nl: "Marketingplan", en: "Marketing plan" } },
      { value: "12 mnd", label: { nl: "Strategische roadmap", en: "Strategic roadmap" } },
      { value: "ROI", label: { nl: "Business case", en: "Business case" } },
    ],
    description: {
      nl: "Fietsenwinkel.nl is een van de grootste fietsenverkopers in Nederland. Voor de lancering van hun eigen merk Brinckers - kwalitatieve fietsen en e-bikes - was een doordacht marketingplan nodig.\n\nWe leverden een compleet online marketingplan op: social media advertising strategie, tracking setup, A/B testing plan, keyword onderzoek, SEA campagnes, display retargeting, SEO aanbevelingen, e-mailmarketing strategie en een business case met omzetprognose en ROI.\n\nEen strategisch fundament waarmee Brinckers met vertrouwen de markt op kan.",
      en: "Fietsenwinkel.nl is one of the largest bicycle retailers in the Netherlands. For the launch of their own brand Brinckers - quality bikes and e-bikes - a well-thought-out marketing plan was needed.\n\nWe delivered a complete online marketing plan: social media advertising strategy, tracking setup, A/B testing plan, keyword research, SEA campaigns, display retargeting, SEO recommendations, email marketing strategy, and a business case with revenue forecast and ROI.\n\nA strategic foundation enabling Brinckers to enter the market with confidence.",
    },
    channels: ["Meta Advertising", "Google Ads", "Google Analytics", "Google Tag Manager"],
  },
  {
    slug: "funem-studio",
    name: "Fünem Studio",
    tagline: {
      nl: "Ambachtelijk weefgerei voor een internationale niche",
      en: "Artisan weaving supplies for an international niche",
    },
    heroImage: "/cases/funem-studio.webp",
    vertical: "Home & Living",
    services: ["Meta Advertising"],
    goals: {
      nl: [
        "Specifieke internationale niche-doelgroep bereiken",
        "Online omzet verhogen via social advertising",
        "Klantanalyse inzetten voor gerichte campagnes",
      ],
      en: [
        "Reach specific international niche audience",
        "Increase online revenue through social advertising",
        "Use customer analysis for targeted campaigns",
      ],
    },
    approach: {
      nl: [
        "Analyse klantenbestand en websiteverkeer",
        "Experimenten om niche-doelgroep internationaal te vinden",
        "Social advertising gericht op weef-enthousiastelingen",
        "Conversieoptimalisatie van de webshop",
      ],
      en: [
        "Analysis of customer base and website traffic",
        "Experiments to find niche audience internationally",
        "Social advertising targeting weaving enthusiasts",
        "Conversion optimization of the webshop",
      ],
    },
    results: [
      { value: "Gevonden", label: { nl: "Internationale niche", en: "International niche" } },
      { value: "Wereldwijd", label: { nl: "Bereik", en: "Reach" } },
      { value: "Groeiend", label: { nl: "Online omzet", en: "Online revenue" } },
    ],
    description: {
      nl: "Fūnem Studio is opgericht door modeontwerper Jantine van Peski om haar liefde voor textiel en weven te delen. Vanuit haar studio in Antwerpen selecteert ze alle vezels met de hand en ontwikkelt ze eigen gereedschappen.\n\nDe producten zijn geliefd binnen een zeer specifieke niche die niet gebonden is aan land, regio of leeftijd. Via een gedetailleerde analyse van het klantenbestand en gerichte experimenten vonden we de juiste doelgroep in een internationale markt.\n\nFünem Studio bereikt nu weef-enthousiastelingen wereldwijd via gerichte social advertising.",
      en: "Fūnem Studio was founded by fashion designer Jantine van Peski to share her love for textiles and weaving. From her studio in Antwerp, she hand-selects all fibers and develops her own tools.\n\nThe products are beloved within a very specific niche not bound by country, region, or age. Through a detailed analysis of the customer base and targeted experiments, we found the right audience in an international market.\n\nFünem Studio now reaches weaving enthusiasts worldwide through targeted social advertising.",
    },
    channels: ["Meta Advertising", "Google Analytics", "Google Tag Manager"],
    website: "https://funemstudio.com/",
  },
  {
    slug: "equals-academy",
    name: "Equals Academy",
    tagline: {
      nl: "1 miljoen vrouwen bereikt voor gratis tech-opleidingen",
      en: "1 million women reached for free tech courses",
    },
    heroImage: "/cases/equals-academy.jpg",
    vertical: "Educatie",
    services: ["Meta Advertising", "TikTok Advertising", "Google Ads", "LinkedIn Advertising"],
    goals: {
      nl: [
        "Leads genereren voor gratis tech-opleidingen",
        "Zo laag mogelijke cost per lead realiseren",
        "Uitbreiden van Amsterdam naar meerdere steden",
      ],
      en: [
        "Generate leads for free tech courses",
        "Achieve the lowest possible cost per lead",
        "Expand from Amsterdam to multiple cities",
      ],
    },
    approach: {
      nl: [
        "Cross-channel strategie: META, TikTok, Google en LinkedIn",
        "Optimale doelgroep geïdentificeerd per stad",
        "Ad content getest op basis van carrière-switching motivatie",
        "Opgeschaald naar meerdere Nederlandse steden",
      ],
      en: [
        "Cross-channel strategy: META, TikTok, Google, and LinkedIn",
        "Identified optimal audience per city",
        "Tested ad content based on career-switching motivation",
        "Scaled to multiple Dutch cities",
      ],
    },
    results: [
      { value: "1M+", label: { nl: "Vrouwen bereikt", en: "Women reached" } },
      { value: "Laag", label: { nl: "Cost per lead", en: "Cost per lead" } },
      { value: "Meerdere", label: { nl: "Steden actief", en: "Cities active" } },
    ],
    description: {
      nl: "Equals streeft naar meer gelijkheid en diversiteit op de werkvloer. Via hun Academy helpen ze vrouwen om een carrière in de IT te starten met kwalitatieve, gratis tech-opleidingen.\n\nSinds 2022 helpen we Equals met lead gen campagnes op META, TikTok, Google Ads en LinkedIn. We testen continu met content die inspeelt op de motivatie van carrière-switchers.\n\nHet resultaat: meer dan 1 miljoen vrouwen bereikt, een lage cost per lead en succesvolle uitbreiding van Amsterdam naar meerdere Nederlandse steden.",
      en: "Equals strives for more equality and diversity in the workplace. Through their Academy, they help women start a career in IT with quality, free tech courses.\n\nSince 2022, we've been helping Equals with lead gen campaigns on META, TikTok, Google Ads, and LinkedIn. We continuously test content that taps into career-switchers' motivation.\n\nThe result: over 1 million women reached, a low cost per lead, and successful expansion from Amsterdam to multiple Dutch cities.",
    },
    channels: ["Meta Advertising", "TikTok Advertising", "Google Ads", "LinkedIn Advertising", "Databox", "Google Analytics", "Google Tag Manager"],
    website: "https://equals.nl/",
    resultsTitle: {
      nl: "Van awareness naar tech-carri\u00e8res.",
      en: "From awareness to tech careers.",
    },
    timeline: [
      { marker: "2022", title: { nl: "Start samenwerking", en: "Partnership starts" }, text: { nl: "Online campagnes voor events, memberships en branding, met de focus op leadgeneratie voor Equals Academy.", en: "Online campaigns for events, memberships and branding, focused on lead generation for Equals Academy." } },
      { marker: "Testen", title: { nl: "Doelgroepen en content", en: "Audiences and content" }, text: { nl: "Verschillende contentvormen en A/B-tests om de best presterende doelgroepen te vinden.", en: "Different content formats and A/B tests to find the best performing audiences." } },
      { marker: "Leadgen", title: { nl: "Meta Lead Ads + landingpages", en: "Meta Lead Ads + landing pages" }, text: { nl: "Leads direct via Instagram en Facebook, met stap voor stap verbeterde landingpages voor hogere conversie.", en: "Leads directly via Instagram and Facebook, with step-by-step improved landing pages for higher conversion." } },
      { marker: "1 mln+", title: { nl: "Vrouwen bereikt", en: "Women reached" }, text: { nl: "Meer dan 1 miljoen vrouwen in Nederland bereikt; vele opleidingen afgerond en tech-carri\u00e8res geboost.", en: "Over 1 million women reached in the Netherlands; many courses completed and tech careers boosted." } },
    ],
    story: {
      nl: [
        "Equals streeft naar meer gelijkheid en diversiteit op de werkvloer. Via de Equals Academy helpen ze vrouwen een carri\u00e8re in de IT te starten, met kwalitatieve tech-opleidingen voor vrouwen die zich willen omscholen naar deze kansrijke sector. Vrouwen maken momenteel 18% uit van de mensen die werkzaam zijn in de IT.",
        "Wrkt helpt Equals sinds 2022 met online campagnes voor events, memberships en branding. De focus ligt op de leadgen-campagnes voor Equals Academy: zoveel mogelijk vrouwen in de doelgroep laten zien welke kansen er in tech liggen. Na het succesvol afronden van een van de gratis opleidingen kunnen ze gegarandeerd aan de slag als tech-professional.",
        "We zetten verschillende strategie\u00ebn en kanalen in. Verschillende contentvormen en A/B-tests vinden de best presterende doelgroepen, landingpages worden stap voor stap verbeterd voor hogere conversieratio's, en Meta Lead Ads verzamelen leads direct via Instagram en Facebook.",
        "De resultaten van alle campagnes zijn inzichtelijk in realtime dashboards, zodat Equals altijd weet of de targets worden gehaald. Het resultaat: snelle groei in het aantal leads, meer dan 1 miljoen vrouwen in Nederland bereikt, en vele afgeronde opleidingen en gebooste tech-carri\u00e8res.",
      ],
      en: [
        "Equals strives for more equality and diversity in the workplace. Through Equals Academy they help women start a career in IT, with high-quality tech courses for women who want to retrain into this promising sector. Women currently make up 18% of people working in IT.",
        "Wrkt has been helping Equals since 2022 with online campaigns for events, memberships and branding. The focus is on lead generation campaigns for Equals Academy: showing as many women in the target group as possible what opportunities tech has to offer. After successfully completing one of the free courses, they are guaranteed to start as a tech professional.",
        "We deploy multiple strategies and channels. Different content formats and A/B tests find the best performing audiences, landing pages are improved step by step for higher conversion rates, and Meta Lead Ads collect leads directly via Instagram and Facebook.",
        "The results of all campaigns are visible in realtime dashboards, so Equals always knows whether targets are being met. The result: rapid lead growth, over 1 million women reached in the Netherlands, and many completed courses and boosted tech careers.",
      ],
    },
  },
  {
    slug: "qisum",
    name: "Qisum",
    tagline: {
      nl: "Exclusief muziekplatform voor Nederlandse artiesten",
      en: "Exclusive music platform for Dutch artists",
    },
    heroImage: "/cases/qisum-new.jpg",
    vertical: "Entertainment",
    services: ["Meta Advertising", "TikTok Advertising", "Google Ads"],
    goals: {
      nl: [
        "Juiste muziekliefhebbers naar het platform trekken",
        "Merkbekendheid verhogen bij breed publiek",
        "Aantal subscribers structureel verbeteren",
      ],
      en: [
        "Attract the right music lovers to the platform",
        "Increase brand awareness with a broad audience",
        "Structurally improve subscriber numbers",
      ],
    },
    approach: {
      nl: [
        "Cross-channel campagnes op META, TikTok en Google",
        "Content testing per artiest en muziekgenre",
        "Subscriber funnel geoptimaliseerd",
        "Real-time dashboards voor campagne-inzichten",
      ],
      en: [
        "Cross-channel campaigns on META, TikTok, and Google",
        "Content testing per artist and music genre",
        "Optimized subscriber funnel",
        "Real-time dashboards for campaign insights",
      ],
    },
    results: [
      { value: "1M+", label: { nl: "Mensen bereikt", en: "People reached" } },
      { value: "Groeiend", label: { nl: "Subscribers", en: "Subscribers" } },
      { value: "300%", label: { nl: "Traffic groei", en: "Traffic growth" } },
    ],
    description: {
      nl: "Qisum viert de Nederlandse muziekcultuur door artiesten centraal te zetten. Het platform biedt exclusieve content van de beste Nederlandse artiesten, van Lee Towers tot Davina Michelle en Armin van Buuren.\n\nSinds november 2023 werken we samen om de juiste doelgroep te bereiken en het aantal subscribers te verhogen. Door veel te testen per artiest en genre leren we continu wat de doelgroep beweegt.\n\nHet bereik is gegroeid tot meer dan 1 miljoen mensen en de traffic is verdrievoudigd. Dashboards geven helder inzicht in welke campagnes het beste presteren.",
      en: "Qisum celebrates Dutch music culture by putting artists center stage. The platform offers exclusive content from the best Dutch artists, from Lee Towers to Davina Michelle and Armin van Buuren.\n\nSince November 2023, we've been working together to reach the right audience and increase subscriber numbers. By testing extensively per artist and genre, we continuously learn what drives the audience.\n\nReach has grown to over 1 million people and traffic has tripled. Dashboards provide clear insight into which campaigns perform best.",
    },
    channels: ["Meta Advertising", "TikTok Advertising", "Google Ads", "Databox", "Google Analytics", "Google Tag Manager"],
    website: "https://www.qisum.nl/",
  },
  {
    slug: "neutra",
    name: "Neutra",
    tagline: {
      nl: "Premium sneakers die fluisteren, niet schreeuwen",
      en: "Premium sneakers that whisper, not shout",
    },
    heroImage: "/cases/neutra.webp",
    vertical: "Fashion & Sieraden",
    services: ["Meta Advertising", "Google Ads"],
    goals: {
      nl: [
        "Merk positioneren bij designbewust publiek",
        "Online omzet structureel verhogen",
        "Eerste stappen richting internationale expansie",
      ],
      en: [
        "Position brand with design-conscious audience",
        "Structurally increase online revenue",
        "First steps toward international expansion",
      ],
    },
    approach: {
      nl: [
        "Gerichte paid advertising op META en Google Ads",
        "Doelgroeptesting: designliefhebbers en sneaker-enthousiastelingen",
        "Content A/B tests met lifestyle vs. product focus",
        "Retargeting op basis van websitegedrag",
      ],
      en: [
        "Targeted paid advertising on META and Google Ads",
        "Audience testing: design lovers and sneaker enthusiasts",
        "Content A/B tests with lifestyle vs. product focus",
        "Retargeting based on website behavior",
      ],
    },
    results: [
      { label: { nl: "Omzetgroei", en: "Revenue growth" }, value: "150%" },
      { label: { nl: "Mensen bereikt", en: "People reached" }, value: "800K+" },
      { label: { nl: "Markten", en: "Markets" }, value: "EU-breed" },
    ],
    description: {
      nl: "Neutra gelooft dat design moet fluisteren, niet schreeuwen. Hun Hakone collectie combineert Japanse esthetiek met Europees vakmanschap - schoenen die hun plek verdienen door doel, aanwezigheid en integriteit.\n\nMet gerichte advertising op META en Google Ads vonden we de optimale combinatie van doelgroep en content. Lifestyle content presteert bijzonder goed bij designbewuste consumenten.\n\nDe online omzet groeide met 150%, we bereikten meer dan 800.000 mensen en zetten de eerste succesvolle stappen richting Europese expansie.",
      en: "Neutra believes design should whisper, not shout. Their Hakone collection combines Japanese aesthetics with European craftsmanship - shoes that earn their place through purpose, presence, and integrity.\n\nWith targeted advertising on META and Google Ads, we found the optimal combination of audience and content. Lifestyle content performs exceptionally well with design-conscious consumers.\n\nOnline revenue grew by 150%, we reached over 800,000 people, and took the first successful steps toward European expansion.",
    },
    channels: ["Meta Advertising", "Google Ads", "Databox", "Google Analytics", "Google Tag Manager"],
    website: "https://neutra.com/",
  },
  {
    slug: "newhouse-gallery",
    name: "NewHouse Gallery",
    tagline: {
      nl: "Hedendaagse kunst toegankelijk online verkopen",
      en: "Making contemporary art accessible online",
    },
    heroImage: "/cases/newhouse-gallery.jpg",
    vertical: "Home & Living",
    services: ["Meta Advertising", "Google Ads"],
    goals: {
      nl: [
        "Online verkoop van kunstwerken en prints stimuleren",
        "Nieuwe doelgroepen aanboren buiten bestaande galeriebezoekers",
        "Merk positioneren bij online kunst-kopers",
      ],
      en: [
        "Stimulate online sales of artworks and prints",
        "Tap into new audiences beyond existing gallery visitors",
        "Position brand with online art buyers",
      ],
    },
    approach: {
      nl: [
        "Doelgroeponderzoek op basis van bestaande klantdata",
        "Visueel sterke campagnes op META",
        "Google Ads voor zoekintentie kunstwerken en prints",
        "Retargeting voor galerie- en websitebezoekers",
      ],
      en: [
        "Audience research based on existing customer data",
        "Visually strong campaigns on META",
        "Google Ads for search intent artworks and prints",
        "Retargeting for gallery and website visitors",
      ],
    },
    results: [
      { label: { nl: "Online verkoop", en: "Online sales" }, value: "Groeiend" },
      { label: { nl: "Mensen bereikt", en: "People reached" }, value: "500K+" },
      { label: { nl: "Nieuw publiek", en: "New audience" }, value: "Online kopers" },
    ],
    description: {
      nl: "NewHouse Gallery maakt hedendaagse kunst en prints toegankelijk voor een breed publiek. Naast de fysieke galerie wilden ze een sterke online verkoopkanaal opbouwen.\n\nMet visueel sterke campagnes op META en Google Ads bereiken we kunstliefhebbers die niet per se de galerie bezoeken. Door te testen met verschillende content formats en doelgroepen vonden we de optimale aanpak.\n\nDe online verkoop groeit en het bereik is uitgebreid tot meer dan 500.000 mensen - een heel nieuw publiek naast de vaste galeriebezoekers.",
      en: "NewHouse Gallery makes contemporary art and prints accessible to a wide audience. In addition to the physical gallery, they wanted to build a strong online sales channel.\n\nWith visually strong campaigns on META and Google Ads, we reach art enthusiasts who don't necessarily visit the gallery. By testing different content formats and audiences, we found the optimal approach.\n\nOnline sales are growing and reach has expanded to over 500,000 people - an entirely new audience alongside regular gallery visitors.",
    },
    channels: ["Meta Advertising", "Google Ads", "Databox", "Google Analytics", "Google Tag Manager"],
    website: "https://www.newhouse-gallery.com/",
  },
];
